import React, { useState, useEffect } from 'react';
import paymentService from 'services/paymentService';
import Swal from 'sweetalert2';
import 'styles/provider.css';

export const CalendarView = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const monthNames = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  useEffect(() => {
    loadPayments();
  }, [currentMonth, currentYear]);

  const loadPayments = async () => {
    try {
      setLoading(true);

      const startDate = new Date(currentYear, currentMonth, 1).toISOString().split('T')[0];
      const endDate = new Date(currentYear, currentMonth + 1, 0).toISOString().split('T')[0];

      const response = await paymentService.getPaymentCalendar({
        fecha_inicio: startDate,
        fecha_fin: endDate
      });


      if (response?.data?.success) {
        const paymentsData = response.data.data.payments;
        setPayments(Array.isArray(paymentsData) ? paymentsData : []);
      }
    } catch (error) {
      console.error('Error al cargar pagos:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudieron cargar los pagos programados',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
      maximumFractionDigits: 0
    }).format(amount || 0);
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOfMonth = (month, year) => {
    return new Date(year, month, 1).getDay();
  };

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getPaymentsForDate = (day) => {
    const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return payments.filter(payment => {
      const paymentDate = payment.fecha_pago_programada?.split('T')[0];
      return paymentDate === dateStr;
    });
  };

  const scheduledPayments = payments
    .filter(payment => payment.estatus === 'aprobada')
    .sort((a, b) => new Date(a.fecha_pago_programada) - new Date(b.fecha_pago_programada));

  const renderCalendar = () => {
    const daysInMonth = getDaysInMonth(currentMonth, currentYear);
    const firstDay = getFirstDayOfMonth(currentMonth, currentYear);
    const days = [];

    // Días vacíos antes del primer día
    for (let i = 0; i < firstDay; i++) {
      days.push(
        <div key={`empty-${i}`} className="calendar-day calendar-day-empty"></div>
      );
    }

    // Días del mes
    for (let day = 1; day <= daysInMonth; day++) {
      const dayPayments = getPaymentsForDate(day);
      const totalAmount = dayPayments.reduce((sum, payment) => sum + (parseFloat(payment.total) || 0), 0);
      const hasPayment = dayPayments.length > 0;

      days.push(
        <div
          key={day}
          className={`calendar-day ${hasPayment ? 'has-payment' : ''}`}
          title={hasPayment ? `${dayPayments.length} pago(s) - ${formatCurrency(totalAmount)}` : ''}
        >
          <div className="calendar-day-number">{day}</div>
          {hasPayment && (
            <>
              <div className="calendar-day-count">{dayPayments.length}</div>
              <div className="calendar-day-amount">
                {formatCurrency(totalAmount)}
              </div>
            </>
          )}
        </div>
      );
    }

    return days;
  };

  if (loading) {
    return (
      <div className="calendar-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando calendario...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="calendar-view">
      <div className="calendar-container">
        <div className="calendar-header">
          <div className="calendar-month">
            {monthNames[currentMonth]} {currentYear}
          </div>
          <div className="calendar-nav">
            <button className="calendar-nav-btn" onClick={handlePrevMonth}>
              ←
            </button>
            <button className="calendar-nav-btn" onClick={handleNextMonth}>
              →
            </button>
          </div>
        </div>

        <div className="calendar-grid">
          <div className="calendar-day-header">Dom</div>
          <div className="calendar-day-header">Lun</div>
          <div className="calendar-day-header">Mar</div>
          <div className="calendar-day-header">Mié</div>
          <div className="calendar-day-header">Jue</div>
          <div className="calendar-day-header">Vie</div>
          <div className="calendar-day-header">Sáb</div>
          {renderCalendar()}
        </div>
      </div>

      <div className="card scheduled-payments-card">
        <div className="card-header">
          Pagos Programados ({scheduledPayments.length})
        </div>
        <div className="scheduled-payments">
          {scheduledPayments.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">  <i className="bi bi-calendar-event"></i></div>
              <div className="empty-text">No hay pagos programados</div>
            </div>
          ) : (
            scheduledPayments.map(payment => (
              <div key={payment.id} className="scheduled-payment-item">
                <div className="payment-info">
                  <div className="payment-folio">{payment.folio}</div>
                  <div className="payment-description">
                    {payment.proveedor_nombre || 'Proveedor'}
                  </div>
                </div>
                <div className="payment-details">
                  <div className="payment-amount">{formatCurrency(payment.total)}</div>
                  <div className="payment-date">
                    {formatDate(payment.fecha_pago_programada)}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
